/**
 * Whiteboard panel: canvas and toolbar for the dedicated whiteboard surface.
 * Receives refs and handlers from useWhiteboard (whiteboard surface only).
 */

import React, { useState } from 'react';
import { Pencil, Eraser, Trash2 } from 'lucide-react';
import { SURFACES, PEN_COLORS } from './constants';
import RemoteCursor from './RemoteCursor';
import './WhiteboardPanel.css';

function WhiteboardPanel({
  whiteboardCanvasRef,
  whiteboardContainerRef,
  getPointerHandlers,
  tool,
  setTool,
  penColor,
  setPenColor,
  isActive,
  setIsActive,
  clearSurface,
  clearAll,
  isTutor = false,
  remoteCursor = null,
}) {
  const [colorPaletteOpen, setColorPaletteOpen] = useState(false);
  const pointerHandlers = getPointerHandlers ? getPointerHandlers(SURFACES.WHITEBOARD) : {};

  const handleColorSelect = (color) => {
    setPenColor?.(color);
    setColorPaletteOpen(false);
  };

  return (
    <div className="whiteboard-panel">
      <div className="whiteboard-toolbar">
        <button
          type="button"
          className={`whiteboard-toolbar-btn ${tool === 'pen' ? 'active' : ''}`}
          onClick={() => setTool('pen')}
          title="Pen"
          aria-pressed={tool === 'pen'}
          disabled={!isActive}
        >
          <Pencil size={18} />
        </button>
        <button
          type="button"
          className={`whiteboard-toolbar-btn ${tool === 'eraser' ? 'active' : ''}`}
          onClick={() => setTool('eraser')}
          title="Eraser"
          aria-pressed={tool === 'eraser'}
          disabled={!isActive}
        >
          <Eraser size={18} />
        </button>
        {penColor != null && setPenColor && (
          <div className="whiteboard-color-palette-wrapper">
            <button
              type="button"
              className={`whiteboard-color-trigger ${colorPaletteOpen ? 'open' : ''}`}
              style={{ backgroundColor: penColor }}
              onClick={() => setColorPaletteOpen((prev) => !prev)}
              title={colorPaletteOpen ? 'Close color palette' : 'Open color palette'}
              disabled={!isActive}
              aria-expanded={colorPaletteOpen}
            />
            {colorPaletteOpen && (
              <div className="whiteboard-color-palette">
                {PEN_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`whiteboard-color-swatch ${penColor === color ? 'active' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleColorSelect(color)}
                    title={`Color: ${color}`}
                    disabled={!isActive}
                    aria-pressed={penColor === color}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {clearAll && isTutor && (
          <button
            type="button"
            className="whiteboard-toolbar-btn"
            onClick={clearAll}
            title="Clear all (whiteboard and lesson overlay)"
            disabled={!isActive}
          >
            <Trash2 size={18} />
            <span>Clear all</span>
          </button>
        )}
      </div>
      <div
        ref={whiteboardContainerRef}
        className="whiteboard-canvas-container"
        style={{
          pointerEvents: isActive ? 'auto' : 'none',
          cursor: isActive && tool === 'pen'
            ? 'url(/cursors/pen.svg) 4 20, crosshair'
            : isActive && tool === 'eraser'
              ? 'url(/cursors/eraser.svg) 12 12, cell'
              : undefined,
        }}
      >
        <canvas
          ref={whiteboardCanvasRef}
          className="whiteboard-canvas"
          onContextMenu={(e) => e.preventDefault()}
          {...pointerHandlers}
        />
        {whiteboardContainerRef && remoteCursor?.whiteboard != null && (
          <RemoteCursor containerRef={whiteboardContainerRef} cursor={remoteCursor.whiteboard} />
        )}
      </div>
    </div>
  );
}

export default React.memo(WhiteboardPanel);
